import { GoldVisionDeal } from '@/models/GoldVision/GoldVisionDeal';

import { ActionType, getType } from 'typesafe-actions';

import { EntityTrade } from '../../../models/EntityTrade';

import { DealDocument, DealTrade, ExecutionCriteriaItem } from '../types';

import * as dealActions from './actions';
import { AllHedgingInstruments } from './types';

type DealActionType = ActionType<typeof dealActions>;

export type DealState = {
  goldVisionDeal: GoldVisionDeal | undefined;
  goldVisionDealLoading: boolean;
  newDealLoading: boolean;
  dealTrades: DealTrade[];
  dealTradesLoading: boolean;
  dealDocuments: DealDocument[];
  dealDocumentsLoading: boolean;
  uploadDealDocumentLoading: boolean;
  entityTrades: EntityTrade[];
  entityTradesLoading: boolean;
  saveAddTradesLoading: boolean;
  allHedgingInstruments: AllHedgingInstruments[];
  allHedgingInstrumentsLoading: boolean;
  dealSummaryDownloading: boolean;
  partialDealDetailsLoading: boolean;
  downloadDealTermSheetLoading: boolean;
  executionCriteria: ExecutionCriteriaItem[];
  executionCriteriaLoading: boolean;
};

const initialState: DealState = {
  goldVisionDeal: undefined,
  goldVisionDealLoading: false,
  newDealLoading: false,
  dealTrades: [],
  dealTradesLoading: false,
  dealDocuments: [],
  dealDocumentsLoading: false,
  uploadDealDocumentLoading: false,
  entityTrades: [],
  entityTradesLoading: false,
  saveAddTradesLoading: false,
  allHedgingInstruments: [],
  allHedgingInstrumentsLoading: false,
  dealSummaryDownloading: false,
  partialDealDetailsLoading: false,
  downloadDealTermSheetLoading: false,
  executionCriteria: [],
  executionCriteriaLoading: false,
};

export const dealReducer = (
  state: DealState = initialState,
  action: DealActionType,
): DealState => {
  switch (action.type) {
    case getType(dealActions.getGoldVisionDeal.request):
      return {
        ...state,
        goldVisionDealLoading: true,
      };
    case getType(dealActions.getGoldVisionDeal.success): {
      return {
        ...state,
        goldVisionDeal: action.payload?.data,
        goldVisionDealLoading: false,
      };
    }
    case getType(dealActions.getGoldVisionDeal.failure):
      return {
        ...state,
        goldVisionDealLoading: false,
      };

    case getType(dealActions.getDealTradesByGVId.request):
      return {
        ...state,
        dealTrades: [],
        dealTradesLoading: true,
      };
    case getType(dealActions.getDealTradesByGVId.success):
      return {
        ...state,
        dealTrades: action.payload.data,
        dealTradesLoading: false,
      };
    case getType(dealActions.getDealTradesByGVId.failure):
      return {
        ...state,
        dealTradesLoading: false,
      };

    case getType(dealActions.getDealDocuments.request):
      return {
        ...state,
        dealDocuments: [],
        dealDocumentsLoading: true,
      };
    case getType(dealActions.getDealDocuments.success):
      return {
        ...state,
        dealDocuments: action.payload.data,
        dealDocumentsLoading: false,
      };
    case getType(dealActions.getDealDocuments.failure):
      return {
        ...state,
        dealDocumentsLoading: false,
      };

    case getType(dealActions.uploadDealDocument.request):
      return {
        ...state,
        uploadDealDocumentLoading: true,
      };
    case getType(dealActions.uploadDealDocument.success):
    case getType(dealActions.uploadDealDocument.failure):
      return {
        ...state,
        uploadDealDocumentLoading: false,
      };
    //

    case getType(dealActions.getEntityTrades.request):
      return {
        ...state,
        entityTrades: [],
        entityTradesLoading: true,
      };
    case getType(dealActions.getEntityTrades.success):
      return {
        ...state,
        entityTrades: action.payload.data,
        entityTradesLoading: false,
      };
    case getType(dealActions.getEntityTrades.failure):
      return {
        ...state,
        entityTrades: [],
        entityTradesLoading: false,
      };

    case getType(dealActions.addEntityTradesToDeal.request):
    case getType(dealActions.createTradeWithPricer.request):
      return {
        ...state,
        saveAddTradesLoading: true,
      };
    case getType(dealActions.addEntityTradesToDeal.success):
    case getType(dealActions.addEntityTradesToDeal.failure):
    case getType(dealActions.createTradeWithPricer.success):
    case getType(dealActions.createTradeWithPricer.failure):
      return {
        ...state,
        saveAddTradesLoading: false,
      };

    case getType(dealActions.getAllHedgingInstruments.request):
      return {
        ...state,
        allHedgingInstruments: [],
        allHedgingInstrumentsLoading: true,
      };
    case getType(dealActions.getAllHedgingInstruments.success):
      return {
        ...state,
        allHedgingInstruments: action.payload.data,
        allHedgingInstrumentsLoading: false,
      };
    case getType(dealActions.getAllHedgingInstruments.failure):
      return {
        ...state,
        allHedgingInstruments: [],
        allHedgingInstrumentsLoading: false,
      };

    //deal summary report download
    case getType(dealActions.downloadDealSummaryReport.request):
      return {
        ...state,
        dealSummaryDownloading: true,
      };
    case getType(dealActions.downloadDealSummaryReport.success):
      return {
        ...state,
        dealSummaryDownloading: false,
      };
    case getType(dealActions.downloadDealSummaryReport.failure):
      return {
        ...state,
        dealSummaryDownloading: false,
      };

    // new deal
    case getType(dealActions.createOrEditDealDetails.request):
      return {
        ...state,
        newDealLoading: true,
      };
    case getType(dealActions.createOrEditDealDetails.success):
      return {
        ...state,
        newDealLoading: false,
      };
    case getType(dealActions.createOrEditDealDetails.failure):
      return {
        ...state,
        newDealLoading: false,
      };

    // update partial deal details
    case getType(dealActions.updatePartialDealDetails.request):
      return {
        ...state,
        partialDealDetailsLoading: true,
      };
    case getType(dealActions.updatePartialDealDetails.success):
      return {
        ...state,
        partialDealDetailsLoading: false,
      };
    case getType(dealActions.updatePartialDealDetails.failure):
      return {
        ...state,
        partialDealDetailsLoading: false,
      };

    // download deal term sheets
    case getType(dealActions.downloadDealTermSheet.request):
      return {
        ...state,
        downloadDealTermSheetLoading: true,
      };
    case getType(dealActions.downloadDealTermSheet.success):
      return {
        ...state,
        downloadDealTermSheetLoading: false,
      };
    case getType(dealActions.downloadDealTermSheet.failure):
      return {
        ...state,
        downloadDealTermSheetLoading: false,
      };

    // execution criteria
    case getType(dealActions.getExecutionCriteria.request):
      return {
        ...state,
        executionCriteria: [],
        executionCriteriaLoading: true,
      };
    case getType(dealActions.getExecutionCriteria.success):
      return {
        ...state,
        executionCriteria: action.payload.data,
        executionCriteriaLoading: false,
      };
    case getType(dealActions.getExecutionCriteria.failure):
      return {
        ...state,
        executionCriteriaLoading: false,
      };

    default:
      return state;
  }
};

export default dealReducer;
